import { EDITOR_FRAME_WIDTH_PX } from './printableWidth';

/**
 * Generates a configuration object for a rich text editor with additional buttons.
 *
 * @hook
 *
 * @param {Array} extraButtons - An array of extra button configurations to include in the editor.
 * @param {Array} [leadingButtons=[]] - Buttons placed first in the toolbar, before the standard ones.
 * @param {Object} [options] - Toolbar options.
 * @param {boolean} [options.canEditSource=false] - Whether the HTML source tool is offered; reserved to developers.
 * @param {Object[]} [options.paragraphSpacingButtons=[]] - The paragraph spacing controls, placed after the line height.
 * @returns {Object} Configuration object for the rich text editor.
 */
export const createConfig = (
  extraButtons,
  leadingButtons = [],
  { canEditSource = false, paragraphSpacingButtons = [] } = {}
) => ({
  uploader: {
    insertImageAsBase64URI: true // Use this if you want to store images as base64 URIs
  },
  // The canvas is the printable page: a table dragged to the edge stops at
  // what will actually print, instead of at a 1200 px editor.
  maxWidth: `${EDITOR_FRAME_WIDTH_PX}px`,
  // Built once from `buttons`: the adaptive toolbar swapped it for Jodit's
  // default list below 900 px and pushed every custom button into "⋮".
  toolbarAdaptive: false,
  buttons: [
    ...(leadingButtons.length ? [...leadingButtons, '|'] : []),
    ...(canEditSource ? ['source', '|'] : []),
    'bold',
    'italic',
    'underline',
    'strikethrough',
    '|',
    'ul',
    'ol',
    'hr',
    '|',
    'outdent',
    'indent',
    '|',
    'font',
    'fontsize',
    'lineHeight',
    ...paragraphSpacingButtons,
    'brush',
    'paragraph',
    'table',
    'align',
    '|',
    'image',
    'link',
    '|',
    ...extraButtons,
    '|',
    'print',
    '|',
    'undo',
    'redo'
  ],
  createAttributes: {
    // Paragraphs typed in the editor line up with the ones imported from
    // Word (margin: 0px inline) instead of the browser's 1em; spacing is
    // then added on purpose with the paragraph spacing tool.
    p: {
      style: 'margin: 0px;'
    },
    table: {
      style:
        'width: 100%; border-collapse: collapse; page-break-before: auto; page-break-after: auto;'
    },
    td: {
      style: 'page-break-inside: avoid;'
    },
    tr: {
      style: 'page-break-inside: avoid;'
    },
    th: {
      style: 'page-break-inside: avoid;'
    }
  },
  controls: {
    lineHeight: {
      list: [
        0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9,
        2
      ]
    }
  }
});
